import type { Lang } from '../types';

/**
 * Wording for buying extra hours either side of a stay.
 *
 * <p>The guest-facing half says what will happen and never why it might not. "Ask your host" is the
 * entire message for an hour the rule cannot clear — no mention of another booking, no arrival
 * time, no greyed-out slot implying someone got there first. Explaining the constraint would mean
 * describing the next guest's stay to this one.
 */
export interface StayExtensionText {
  // Guest — the guide section
  // The collapsed header: a question, narrowed to whatever is actually on offer.
  askHeaderBoth: string;
  askHeaderEarly: string;
  askHeaderLate: string;
  // Which times the row is showing — never leave two bare clock values unexplained.
  timesStandard: string;
  timesYours: string;
  timesIn: string;
  timesOut: string;
  // Offer chips. Each line is its own string so none has to fit a sentence.
  chipCheckin: string;
  chipCheckout: string;
  chipFrom: string;
  chipTill: string;
  chipsStillAvailable: string;
  chipsCanStillAsk: string;
  // Live request strips
  reqYours: string;
  reqApproved: string;
  reqNotPossible: string;
  reqWaiting: string;
  remindCta: string;
  remindWait: string;
  remindSent: string;
  // Guest — luggage
  askHeaderLuggage: string;
  luggageDropoffTitle: string;
  luggagePickupTitle: string;
  // Short, chip-width-safe product names, one per luggage product so a drop-off chip and a
  // pick-up chip never render the same label. See utils/stayExtensionChipLabel.ts.
  chipLuggageDropoff: string;
  chipLuggagePickup: string;
  luggageWhereTitle: string;
  // What the guest reads when refused, rendered from the stored code
  dCleaning: string;
  dOtherBooking: string;
  dShortNotice: string;
  dNoSpace: string;
  dUnspecified: string;
  // Manager's decline picker
  declineTitle: string;
  declineGuestSees: string;
  declineCancel: string;
  declineConfirm: string;
  mCleaning: string;
  mOtherBooking: string;
  mShortNotice: string;
  mNoSpace: string;
  mUnspecified: string;
  // The scope line on the decline sheet
  scopeLabelEarly: string;
  scopeLabelLate: string;
  scopeHintEarly: string;
  scopeHintLate: string;
  scopeHintNotice: string;
  scopeAuto: string;
  // The reservation-detail block
  rdTitle: string;
  rdCheckin: string;
  rdCheckout: string;
  rdLuggageDropoff: string;
  rdLuggagePickup: string;
  rdClosedLuggageIn: string;
  rdClosedLuggageOut: string;
  rdStandard: string;
  rdPaid: string;
  rdClosedEarly: string;
  rdClosedLate: string;
  rdClosedDetail: string;
  rdReopen: string;
  rdWaitingManager: string;
  rdWaitingPayment: string;
  earlyTitle: string;
  lateTitle: string;
  instantBadge: string;
  askBadge: string;
  free: string;
  noteLabel: string;
  notePlaceholder: string;
  submitting: string;
  // Guest — request states
  statusPaid: string;
  statusExpired: string;
  payNow: string;
  withdraw: string;
  withdrawConfirmQ: string;
  withdrawConfirmWhy: string;
  withdrawConfirmWhyApproved: string;
  withdrawKeep: string;
  withdrawYes: string;
  quoteUntil: string;
  requestError: string;
  // Guest — the payment page
  payTitleEarly: string;
  payTitleLate: string;
  payTitleLuggageIn: string;
  payTitleLuggageOut: string;
  payLuggageInLabel: string;
  payLuggageOutLabel: string;
  payCta: string;
  payLoading: string;
  payInvalidTitle: string;
  payInvalidBody: string;
  payDone: string;
  payError: string;
  payNothingChanges: string;
  payHeaderTag: string;
  paySecure: string;
  payRowApartment: string;
  payRowDates: string;
  payTotal: string;
  payDoneBody: string;
  payHoursInLabel: string;
  payHoursOutLabel: string;
  // Manager — the "what is this" panel above the settings
  introTitle: string;
  introDesc: string;
  introStep1Label: string;
  introStep1Desc: string;
  introStep2Label: string;
  introStep2Desc: string;
  introStep3Label: string;
  introStep3Desc: string;
  introStep4Label: string;
  introStep4Desc: string;
  introNote: string;
  // Manager — the two products this apartment can sell alongside the stay
  productHours: string;
  productHoursDesc: string;
  productLuggage: string;
  productLuggageDesc: string;
  // Luggage gets its own mode hints: the cleaning window and the neighbouring stay, which are the
  // whole reason an hour is hard to give, say nothing at all about a cupboard.
  luggageOffHint: string;
  luggageAutoHint: string;
  luggageRequestHint: string;
  dropoffStairTitle: string;
  pickupStairTitle: string;
  stairHintDropoff: string;
  stairHintPickup: string;
  luggageWhereLabel: string;
  luggageWhereHint: string;
  luggageWherePlaceholder: string;
  luggageWhereEnLabel: string;
  luggageWhereMissing: string;
  // Manager — settings
  settingsTitle: string;
  settingsIntro: string;
  modeLabel: string;
  modeOff: string;
  modeOffHint: string;
  modeAuto: string;
  modeAutoHint: string;
  modeRequest: string;
  modeRequestHint: string;
  cleaningLabel: string;
  cleaningHint: string;
  noticeLabel: string;
  noticeHint: string;
  noticeZeroHint: string;
  earlyStairTitle: string;
  lateStairTitle: string;
  // The two stairs run in opposite directions, so their wording cannot be shared: an early
  // check-in row is priced *from* its hour forward, a late check-out row *until* its hour.
  stairHintEarly: string;
  stairHintLate: string;
  addRow: string;
  timeLabelFrom: string;
  timeLabelTo: string;
  priceLabel: string;
  noRows: string;
  remove: string;
  saved: string;
  // Manager — the pending queue
  pendingTitle: string;
  pendingEmpty: string;
  pendingIntro: string;
  // The one line a manager reads before anything else: can this be said yes to?
  vFits: string;
  vTight: string;
  vWontFit: string;
  vConflict: string;
  vShortNotice: string;
  vSpare: string;
  vShort: string;
  vNobodyArriving: string;
  vOtherGuestHolds: string;
  vNoticeDaysShort: string;
  /** Luggage has no turnover to weigh, so the queue says why instead of drawing an empty bar. */
  vLuggageNoClash: string;
  // The shared legend above the queue, and the callouts under each bar
  legendThisStay: string;
  legendAsked: string;
  legendNeighbour: string;
  legendCleaning: string;
  legendFolded: string;
  noPaymentAccount: string;
  noPaymentAccountFix: string;
  legendClash: string;
  barOut: string;
  barAskedAt: string;
  barNextIn: string;
  barPrevOut: string;
  barNormal: string;
  approveAnyway: string;
  approveCta: string;
  counterCta: string;
  declineCta: string;
  counterTitle: string;
  counterAsked: string;
  counterPriceLabel: string;
  counterConfirm: string;
  actionError: string;
}

export const stayExtensionText: Record<Lang, StayExtensionText> = {
  en: {
    askHeaderBoth: 'Need earlier check-in or later check-out?',
    askHeaderEarly: 'Need an earlier check-in?',
    askHeaderLate: 'Need a later check-out?',
    timesStandard: 'Standard',
    timesYours: 'Your times',
    timesIn: 'in',
    timesOut: 'out',
    chipCheckin: 'Check-in',
    chipCheckout: 'Check-out',
    chipFrom: 'from {time}',
    chipTill: 'till {time}',
    chipsStillAvailable: 'Still available',
    chipsCanStillAsk: 'You can still ask for',
    reqYours: 'Your request',
    reqApproved: 'Approved by your host',
    reqNotPossible: 'Not possible',
    reqWaiting: 'waiting…',
    remindCta: 'Remind your host',
    remindWait: 'You can remind again after {time}',
    remindSent: 'Your host has been reminded.',
    dCleaning: 'Your host can’t get the apartment ready in time.',
    dOtherBooking: 'The apartment is needed for another booking that day.',
    dShortNotice: 'This is too close to your stay for us to arrange.',
    dUnspecified: 'Unfortunately this isn’t possible on this occasion.',
    declineTitle: 'Decline this request',
    declineGuestSees: 'Guest sees: “{text}”',
    declineCancel: 'Cancel',
    declineConfirm: 'Decline',
    mCleaning: 'Cleaning wouldn’t fit',
    mOtherBooking: 'Needed for another booking',
    mShortNotice: 'Too short notice',
    mUnspecified: 'No reason given',
    scopeLabelEarly: 'Also close later times for this stay',
    scopeLabelLate: 'Also close earlier times for this stay',
    scopeHintEarly: 'They could still ask for a later arrival, which needs less preparation than {time}.',
    scopeHintLate: 'They could still ask for an earlier departure, which leaves more cleaning time than {time}.',
    scopeHintNotice: 'Short notice applies to every hour, not just this one — untick if you would still take a smaller request.',
    scopeAuto: 'auto',
    rdTitle: 'Extra hours',
    rdCheckin: 'Check-in',
    rdCheckout: 'Check-out',
    rdStandard: 'standard',
    rdPaid: 'paid · {price} · {date}',
    rdClosedEarly: 'Earlier check-in closed to new requests',
    rdClosedLate: 'Later check-out closed to new requests',
    rdClosedDetail: 'Declined {date} — {reason} The guest cannot ask again until you reopen it.',
    rdReopen: 'Allow requests again',
    rdWaitingManager: 'asked for {time} · waiting for your answer since {since}',
    rdWaitingPayment: '{time} approved · {price} · waiting for payment until {until}',
    earlyTitle: 'Arrive earlier',
    lateTitle: 'Stay later',
    instantBadge: 'book now',
    askBadge: 'ask host',
    free: 'free',
    noteLabel: 'Anything your host should know?',
    notePlaceholder: 'e.g. flight lands 09:40',
    submitting: 'Sending…',
    statusPaid: 'Confirmed.',
    statusExpired: 'This offer has expired.',
    payNow: 'Confirm and pay',
    withdraw: 'Cancel request',
    withdrawConfirmQ: 'Cancel your request for {time}?',
    withdrawConfirmWhy: 'You can ask again, but it goes to the back of the host’s queue.',
    withdrawConfirmWhyApproved: 'The host already agreed to this. Asking again later may get a different answer or price.',
    withdrawKeep: 'Keep it',
    withdrawYes: 'Cancel it',
    quoteUntil: 'Please confirm by {date}.',
    requestError: 'That did not go through. Please try again.',
    payTitleEarly: 'Early check-in from {time}',
    payTitleLate: 'Late check-out until {time}',
    payCta: 'Pay {price}',
    payLoading: 'Loading…',
    payInvalidTitle: 'This link is no longer valid',
    payInvalidBody: 'It may have been used already, or the offer has expired. Please ask your host for a new one.',
    payDone: 'Paid — your booking has been updated.',
    payError: 'Payment could not be started. Please try again shortly.',
    payNothingChanges: 'Nothing changes until the payment goes through.',
    payHeaderTag: 'Payment request',
    paySecure: 'Payment is processed securely by Stripe.',
    payRowApartment: 'Apartment',
    payRowDates: 'Stay',
    payTotal: 'Total',
    payDoneBody: 'Your booking has been updated. Your host has been told.',
    payHoursInLabel: 'check-in',
    payHoursOutLabel: 'check-out',
    introTitle: 'How selling extra hours works',
    introDesc: 'Guests ask for an earlier arrival or a later departure from their check-in guide. You set the prices, and you decide who gets to say yes.',
    introStep1Label: 'Guest asks',
    introStep1Desc: 'They pick an hour from the prices you set below, in their check-in guide.',
    introStep2Label: 'Someone says yes',
    introStep2Desc: 'You do — or the rule does, if you switch this apartment to automatic.',
    introStep3Label: 'Guest pays',
    introStep3Desc: 'On a page of its own. You can send that link by hand too.',
    introStep4Label: 'Times move',
    introStep4Desc: 'Only now. The door code is reissued and your cleaner sees the new time.',
    introNote: 'Approving is only a quote — nothing about the booking changes until the money arrives.',
    settingsTitle: 'Early check-in & late check-out',
    settingsIntro: 'Sell the hours either side of a stay. Guests ask from their check-in guide.',
    modeLabel: 'Who approves',
    modeOff: 'Off',
    modeOffHint: 'Nothing appears on the guide.',
    modeAuto: 'Automatic',
    modeAutoHint: 'Sell instantly when it is safe. Anything else still comes to you.',
    modeRequest: 'On request',
    modeRequestHint: 'Every ask comes to you.',
    cleaningLabel: 'Cleaning takes',
    cleaningHint: 'Measured against each request. Never hides an hour from the guest — you always get to see the ask.',
    noticeLabel: 'Automatic selling stops',
    noticeHint: 'days before the stay, so your cleaners are never surprised. Later requests come to you instead.',
    noticeZeroHint: '0 = sell right up to the last minute, as long as the cleaning still fits.',
    earlyStairTitle: 'Early check-in prices',
    lateStairTitle: 'Late check-out prices',
    stairHintEarly: 'Each row prices every hour from it up to the next one. The earliest row is also the earliest a guest may ask for.',
    stairHintLate: 'Each row prices every hour up to it, back to the row before. The latest row is also the latest a guest may ask for.',
    addRow: 'Add price',
    timeLabelFrom: 'From',
    timeLabelTo: 'Until',
    priceLabel: 'Price (CZK)',
    noRows: 'No prices yet — guests will not see this option.',
    remove: 'Remove',
    saved: 'Saved',
    pendingTitle: 'Requests waiting',
    pendingEmpty: 'Nothing waiting.',
    pendingIntro: 'Guests hear nothing until you answer, so the quick ones are worth doing first.',
    vFits: 'Fits',
    vTight: 'Tight',
    vWontFit: 'Won’t fit',
    vConflict: 'Conflict',
    vShortNotice: 'Short notice',
    vSpare: '{time} to spare',
    vShort: '{time} short',
    vNobodyArriving: 'nobody arriving that day',
    vOtherGuestHolds: 'another guest holds these hours',
    vNoticeDaysShort: '{days} days notice',
    legendThisStay: 'This stay',
    legendAsked: 'Asked for',
    legendNeighbour: 'Next / previous guest',
    legendCleaning: 'Cleaning',
    legendFolded: 'Empty, shortened',
    noPaymentAccount: 'This apartment cannot take payments yet, so the guest will not be able to pay for these hours.',
    noPaymentAccountFix: 'Set up payments',
    legendClash: 'Clash',
    barOut: 'out {time}',
    barAskedAt: 'asked {time}',
    barNextIn: 'next {time}',
    barPrevOut: 'prev {time}',
    barNormal: 'normal {time}',
    approveAnyway: 'Approve anyway',
    approveCta: 'Approve {time}',
    counterCta: 'Offer another time',
    declineCta: 'Decline',
    counterTitle: 'Offer another time',
    counterAsked: 'asked',
    counterPriceLabel: 'Price for this time',
    counterConfirm: 'Offer {time}',
    actionError: 'That did not work. Please reload and try again.',

    // ── Luggage storage ──────────────────────────────────────────────────
    productHours: 'Extra hours',
    productHoursDesc: 'Sell the hours either side of a stay — an earlier arrival or a later departure. Limited by how long cleaning takes.',
    productLuggage: 'Luggage storage',
    productLuggageDesc: 'Somewhere to leave bags outside the normal hours. Costs you no cleaning time, so you can often say yes here even when you have to say no above.',
    luggageOffHint: 'Not offered.',
    luggageAutoHint: 'Guests book storage instantly at your price.',
    luggageRequestHint: 'Every request comes to you first.',
    dropoffStairTitle: 'Bags can arrive from',
    pickupStairTitle: 'Bags can stay until',
    stairHintDropoff: 'Your earliest row is also the limit — nobody can ask to drop bags before it.',
    stairHintPickup: 'Your latest row is also the limit — nobody can ask to leave bags past it.',
    luggageWhereLabel: 'Where to leave the bags',
    luggageWhereHint: 'Shown only to guests who have paid for storage. Say exactly where to put them and how to get in.',
    luggageWherePlaceholder: 'e.g. In the entrance cupboard on the left, code 1234. Please label your bags.',
    luggageWhereEnLabel: 'English version',
    luggageWhereMissing: 'Guests who buy storage will not be told where to put their bags. Add instructions before your first sale.',
    askHeaderLuggage: 'Need somewhere to leave your bags?',
    luggageDropoffTitle: 'Drop bags off early',
    luggagePickupTitle: 'Leave bags after check-out',
    chipLuggageDropoff: 'Drop-off',
    chipLuggagePickup: 'Pickup',
    luggageWhereTitle: 'Where to leave your bags',
    dNoSpace: 'Your host has no room to store luggage that day.',
    mNoSpace: 'No room for bags',
    rdLuggageDropoff: 'Bag drop-off',
    rdLuggagePickup: 'Bag pick-up',
    rdClosedLuggageIn: 'Early bag drop-off closed to new requests',
    rdClosedLuggageOut: 'Late bag pick-up closed to new requests',
    payTitleLuggageIn: 'Luggage storage from {time}',
    payTitleLuggageOut: 'Luggage storage until {time}',
    payLuggageInLabel: 'Bags from',
    payLuggageOutLabel: 'Bags until',
    vLuggageNoClash: 'Bags only — your turnover is untouched',
  },

  cs: {
    askHeaderBoth: 'Potřebujete dřívější check-in nebo pozdější check-out?',
    askHeaderEarly: 'Potřebujete dřívější check-in?',
    askHeaderLate: 'Potřebujete pozdější check-out?',
    timesStandard: 'Standardně',
    timesYours: 'Vaše časy',
    timesIn: 'příjezd',
    timesOut: 'odjezd',
    chipCheckin: 'Check-in',
    chipCheckout: 'Check-out',
    chipFrom: 'od {time}',
    chipTill: 'do {time}',
    chipsStillAvailable: 'Stále k dispozici',
    chipsCanStillAsk: 'Můžete ještě požádat o',
    reqYours: 'Vaše žádost',
    reqApproved: 'Hostitel schválil',
    reqNotPossible: 'Není možné',
    reqWaiting: 'čeká…',
    remindCta: 'Připomenout hostiteli',
    remindWait: 'Znovu připomenout můžete po {time}',
    remindSent: 'Hostiteli jsme dali vědět.',
    dCleaning: 'Hostitel nestihne byt do té doby připravit.',
    dOtherBooking: 'Byt je ten den potřeba pro jinou rezervaci.',
    dShortNotice: 'Je to příliš blízko vašemu pobytu, než abychom to stihli zařídit.',
    dUnspecified: 'Bohužel to tentokrát není možné.',
    declineTitle: 'Zamítnout žádost',
    declineGuestSees: 'Host uvidí: „{text}“',
    declineCancel: 'Zpět',
    declineConfirm: 'Zamítnout',
    mCleaning: 'Nevešel by se úklid',
    mOtherBooking: 'Byt je potřeba pro jinou rezervaci',
    mShortNotice: 'Příliš pozdě',
    mUnspecified: 'Bez udání důvodu',
    scopeLabelEarly: 'Zavřít i pozdější časy pro tento pobyt',
    scopeLabelLate: 'Zavřít i dřívější časy pro tento pobyt',
    scopeHintEarly: 'Host může ještě požádat o pozdější příjezd, který vyžaduje méně příprav než {time}.',
    scopeHintLate: 'Host může ještě požádat o dřívější odjezd, který nechá na úklid více času než {time}.',
    scopeHintNotice: 'Pozdní žádost se týká všech hodin, nejen této — odškrtněte, pokud byste menší žádost přijali.',
    scopeAuto: 'auto',
    rdTitle: 'Hodiny navíc',
    rdCheckin: 'Check-in',
    rdCheckout: 'Check-out',
    rdStandard: 'standardně',
    rdPaid: 'zaplaceno · {price} · {date}',
    rdClosedEarly: 'Dřívější check-in je uzavřen pro nové žádosti',
    rdClosedLate: 'Pozdější check-out je uzavřen pro nové žádosti',
    rdClosedDetail: 'Zamítnuto {date} — {reason} Host nemůže požádat znovu, dokud to neotevřete.',
    rdReopen: 'Znovu povolit žádosti',
    rdWaitingManager: 'žádá o {time} · čeká na vaši odpověď od {since}',
    rdWaitingPayment: '{time} schváleno · {price} · čeká na platbu do {until}',
    earlyTitle: 'Přijet dřív',
    lateTitle: 'Zůstat déle',
    instantBadge: 'rezervovat',
    askBadge: 'zeptat se',
    free: 'zdarma',
    noteLabel: 'Chcete hostiteli něco vzkázat?',
    notePlaceholder: 'např. letadlo přistává v 9:40',
    submitting: 'Odesílám…',
    statusPaid: 'Potvrzeno.',
    statusExpired: 'Nabídka vypršela.',
    payNow: 'Potvrdit a zaplatit',
    withdraw: 'Zrušit žádost',
    withdrawConfirmQ: 'Zrušit žádost o {time}?',
    withdrawConfirmWhy: 'Můžete požádat znovu, ale zařadíte se na konec fronty.',
    withdrawConfirmWhyApproved: 'Hostitel už souhlasil. Pozdější žádost může dopadnout jinak nebo stát víc.',
    withdrawKeep: 'Ponechat',
    withdrawYes: 'Zrušit',
    quoteUntil: 'Potvrďte prosím do {date}.',
    requestError: 'Nepodařilo se odeslat. Zkuste to prosím znovu.',
    payTitleEarly: 'Dřívější check-in od {time}',
    payTitleLate: 'Pozdější check-out do {time}',
    payCta: 'Zaplatit {price}',
    payLoading: 'Načítám…',
    payInvalidTitle: 'Tento odkaz už neplatí',
    payInvalidBody: 'Možná byl už použit, nebo nabídka vypršela. Požádejte prosím hostitele o nový.',
    payDone: 'Zaplaceno — rezervace byla upravena.',
    payError: 'Platbu se nepodařilo zahájit. Zkuste to prosím za chvíli.',
    payNothingChanges: 'Dokud platba neproběhne, nic se nemění.',
    payHeaderTag: 'Žádost o platbu',
    paySecure: 'Platbu bezpečně zpracovává Stripe.',
    payRowApartment: 'Apartmán',
    payRowDates: 'Pobyt',
    payTotal: 'Celkem',
    payDoneBody: 'Vaše rezervace byla upravena. Hostitel byl informován.',
    payHoursInLabel: 'příjezd',
    payHoursOutLabel: 'odjezd',
    introTitle: 'Jak funguje prodej hodin navíc',
    introDesc: 'Hosté žádají o dřívější příjezd nebo pozdější odjezd přímo v průvodci check-inem. Ceny určujete vy — a také to, kdo žádost schvaluje.',
    introStep1Label: 'Host požádá',
    introStep1Desc: 'Vybere si hodinu z cen, které nastavíte níže, přímo v průvodci.',
    introStep2Label: 'Někdo schválí',
    introStep2Desc: 'Buď vy, nebo pravidlo — pokud u tohoto bytu zapnete automatický režim.',
    introStep3Label: 'Host zaplatí',
    introStep3Desc: 'Na samostatné stránce. Odkaz můžete poslat i ručně.',
    introStep4Label: 'Časy se změní',
    introStep4Desc: 'Až teď. Přegeneruje se kód od dveří a úklid uvidí nový čas.',
    introNote: 'Schválení je jen nabídka — dokud nepřijdou peníze, na rezervaci se nic nemění.',
    settingsTitle: 'Dřívější check-in a pozdější check-out',
    settingsIntro: 'Prodávejte hodiny na obou koncích pobytu. Hosté žádají ze svého průvodce check-inem.',
    modeLabel: 'Kdo schvaluje',
    modeOff: 'Vypnuto',
    modeOffHint: 'V průvodci se nic nezobrazí.',
    modeAuto: 'Automaticky',
    modeAutoHint: 'Prodat hned, když je to bezpečné. Cokoli jiného přijde stejně vám.',
    modeRequest: 'Na žádost',
    modeRequestHint: 'Každá žádost přijde vám.',
    cleaningLabel: 'Úklid trvá',
    cleaningHint: 'Porovnává se s každou žádostí. Hodinu hostovi nikdy neskryje — žádost vždy uvidíte.',
    noticeLabel: 'Automatický prodej končí',
    noticeHint: 'dní před pobytem, aby úklid nic nepřekvapilo. Pozdější žádosti přijdou vám.',
    noticeZeroHint: '0 = prodávat až do poslední chvíle, pokud se úklid pořád vejde.',
    earlyStairTitle: 'Ceny za dřívější check-in',
    lateStairTitle: 'Ceny za pozdější check-out',
    stairHintEarly: 'Každý řádek určuje cenu pro všechny hodiny od sebe k dalšímu řádku. Nejčasnější řádek je zároveň nejdřívější čas, o který lze požádat.',
    stairHintLate: 'Každý řádek určuje cenu pro všechny hodiny až po sebe, zpět k předchozímu řádku. Nejpozdější řádek je zároveň nejzazší čas, o který lze požádat.',
    addRow: 'Přidat cenu',
    timeLabelFrom: 'Od',
    timeLabelTo: 'Do',
    priceLabel: 'Cena (Kč)',
    noRows: 'Zatím žádné ceny — hosté tuto možnost neuvidí.',
    remove: 'Odebrat',
    saved: 'Uloženo',
    pendingTitle: 'Čekající žádosti',
    pendingEmpty: 'Nic nečeká.',
    pendingIntro: 'Dokud neodpovíte, host neví nic — rychlá odpověď se vyplatí.',
    vFits: 'Vejde se',
    vTight: 'Těsné',
    vWontFit: 'Nevejde se',
    vConflict: 'Kolize',
    vShortNotice: 'Málo času předem',
    vSpare: 'zbývá {time}',
    vShort: 'chybí {time}',
    vNobodyArriving: 'ten den nikdo nepřijíždí',
    vOtherGuestHolds: 'tyto hodiny už má jiný host',
    vNoticeDaysShort: '{days} dní předem',
    legendThisStay: 'Tento pobyt',
    legendAsked: 'Žádost',
    legendNeighbour: 'Další / předchozí host',
    legendCleaning: 'Úklid',
    legendFolded: 'Prázdno, zkráceno',
    noPaymentAccount: 'Tento apartmán zatím nemůže přijímat platby, host tyto hodiny nezaplatí.',
    noPaymentAccountFix: 'Nastavit platby',
    legendClash: 'Kolize',
    barOut: 'odjezd {time}',
    barAskedAt: 'žádá {time}',
    barNextIn: 'další {time}',
    barPrevOut: 'předch. {time}',
    barNormal: 'běžně {time}',
    approveAnyway: 'Přesto schválit',
    approveCta: 'Schválit {time}',
    counterCta: 'Nabídnout jiný čas',
    declineCta: 'Zamítnout',
    counterTitle: 'Nabídnout jiný čas',
    counterAsked: 'přání',
    counterPriceLabel: 'Cena za tento čas',
    counterConfirm: 'Nabídnout {time}',
    actionError: 'Nepodařilo se. Načtěte stránku znovu a zkuste to prosím ještě jednou.',

    // ── Úschova zavazadel ────────────────────────────────────────────────
    productHours: 'Hodiny navíc',
    productHoursDesc: 'Prodávejte hodiny před příjezdem a po odjezdu. Omezeno tím, jak dlouho trvá úklid.',
    productLuggage: 'Úschova zavazadel',
    productLuggageDesc: 'Místo pro kufry mimo běžné hodiny. Nestojí vás to žádný čas na úklid, takže tady můžete často vyhovět i ve dnech, kdy hodiny navíc dát nejde.',
    luggageOffHint: 'Nenabízí se.',
    luggageAutoHint: 'Hosté si úschovu koupí okamžitě za vaši cenu.',
    luggageRequestHint: 'Každý požadavek přijde nejdřív vám.',
    dropoffStairTitle: 'Zavazadla lze přinést od',
    pickupStairTitle: 'Zavazadla mohou zůstat do',
    stairHintDropoff: 'Nejčasnější řádek je zároveň hranice — dřív si nikdo říct nemůže.',
    stairHintPickup: 'Nejpozdější řádek je zároveň hranice — později si nikdo říct nemůže.',
    luggageWhereLabel: 'Kam zavazadla uložit',
    luggageWhereHint: 'Uvidí jen hosté, kteří si úschovu zaplatili. Napište přesně kam a jak se dovnitř dostanou.',
    luggageWherePlaceholder: 'např. Ve skříni vlevo ve vstupní chodbě, kód 1234. Označte si prosím zavazadla.',
    luggageWhereEnLabel: 'Anglická verze',
    luggageWhereMissing: 'Hosté, kteří si úschovu koupí, se nedozvědí, kam zavazadla dát. Doplňte pokyny před prvním prodejem.',
    askHeaderLuggage: 'Potřebujete někam odložit zavazadla?',
    luggageDropoffTitle: 'Přinést zavazadla dřív',
    luggagePickupTitle: 'Nechat zavazadla po odjezdu',
    chipLuggageDropoff: 'Odložit',
    chipLuggagePickup: 'Vyzvednout',
    luggageWhereTitle: 'Kam zavazadla uložit',
    dNoSpace: 'Hostitel ten den nemá kam zavazadla uložit.',
    mNoSpace: 'Není místo na zavazadla',
    rdLuggageDropoff: 'Příjem zavazadel',
    rdLuggagePickup: 'Vyzvednutí zavazadel',
    rdClosedLuggageIn: 'Dřívější příjem zavazadel je uzavřen pro nové žádosti',
    rdClosedLuggageOut: 'Pozdější vyzvednutí zavazadel je uzavřeno pro nové žádosti',
    payTitleLuggageIn: 'Úschova zavazadel od {time}',
    payTitleLuggageOut: 'Úschova zavazadel do {time}',
    payLuggageInLabel: 'Zavazadla od',
    payLuggageOutLabel: 'Zavazadla do',
    vLuggageNoClash: 'Jen zavazadla — výměny hostů se to netýká',
  },

  vi: {
    askHeaderBoth: 'Bạn cần nhận phòng sớm hơn hoặc trả phòng muộn hơn?',
    askHeaderEarly: 'Bạn cần nhận phòng sớm hơn?',
    askHeaderLate: 'Bạn cần trả phòng muộn hơn?',
    timesStandard: 'Tiêu chuẩn',
    timesYours: 'Giờ của bạn',
    timesIn: 'nhận',
    timesOut: 'trả',
    chipCheckin: 'Nhận phòng',
    chipCheckout: 'Trả phòng',
    chipFrom: 'từ {time}',
    chipTill: 'đến {time}',
    chipsStillAvailable: 'Vẫn còn',
    chipsCanStillAsk: 'Bạn vẫn có thể xin',
    reqYours: 'Yêu cầu của bạn',
    reqApproved: 'Chủ nhà đã đồng ý',
    reqNotPossible: 'Không thể',
    reqWaiting: 'đang chờ…',
    remindCta: 'Nh𞫼 chủ nhà',
    remindWait: 'Bạn có thể nh𞫼 lại sau {time}',
    remindSent: 'Đã nh𞫼 chủ nhà.',
    dCleaning: 'Chủ nhà không kịp dọn phòng xong trước giờ đó.',
    dOtherBooking: 'Hôm đó căn hộ cần cho một đặt phòng khác.',
    dShortNotice: 'Quá sát ngày ở nên không sắp xếp kịp.',
    dUnspecified: 'Rất tiếc, lần này không thể được.',
    declineTitle: 'Từ chối yêu cầu',
    declineGuestSees: 'Khách sẽ thấy: “{text}”',
    declineCancel: 'Quay lại',
    declineConfirm: 'Từ chối',
    mCleaning: 'Không đủ thời gian dọn',
    mOtherBooking: 'Cần cho đặt phòng khác',
    mShortNotice: 'Báo quá gấp',
    mUnspecified: 'Không nêu lý do',
    scopeLabelEarly: 'Đóng luôn các giờ muộn hơn cho lượt này',
    scopeLabelLate: 'Đóng luôn các giờ sớm hơn cho lượt này',
    scopeHintEarly: 'Khách vẫn có thể xin đến muộn hơn, cần ít thời gian chuẩn bị hơn {time}.',
    scopeHintLate: 'Khách vẫn có thể xin đi sớm hơn, để lại nhiều thời gian dọn hơn {time}.',
    scopeHintNotice: 'Báo gấp áp dụng cho mọi giờ, không riêng giờ này — bỏ chọn nếu bạn vẫn nhận yêu cầu nhỏ hơn.',
    scopeAuto: 'tự động',
    rdTitle: 'Giờ thêm',
    rdCheckin: 'Nhận phòng',
    rdCheckout: 'Trả phòng',
    rdStandard: 'tiêu chuẩn',
    rdPaid: 'đã trả · {price} · {date}',
    rdClosedEarly: 'Nhận phòng sớm đã đóng, không nhận yêu cầu mới',
    rdClosedLate: 'Trả phòng muộn đã đóng, không nhận yêu cầu mới',
    rdClosedDetail: 'Từ chối {date} — {reason} Khách không thể xin lại cho đến khi bạn mở lại.',
    rdReopen: 'Cho phép xin lại',
    rdWaitingManager: 'xin {time} · đang chờ bạn trả lời từ {since}',
    rdWaitingPayment: '{time} đã duyệt · {price} · chờ thanh toán đến {until}',
    earlyTitle: 'Đến sớm hơn',
    lateTitle: 'Ở lại lâu hơn',
    instantBadge: 'đặt ngay',
    askBadge: 'hỏi chủ nhà',
    free: 'miễn phí',
    noteLabel: 'Bạn muốn nhắn gì với chủ nhà không?',
    notePlaceholder: 'ví dụ: chuyến bay hạ cánh 09:40',
    submitting: 'Đang gửi…',
    statusPaid: 'Đã xác nhận.',
    statusExpired: 'Ưu đãi này đã hết hạn.',
    payNow: 'Xác nhận và thanh toán',
    withdraw: 'Hủy yêu cầu',
    withdrawConfirmQ: 'Hủy yêu cầu {time}?',
    withdrawConfirmWhy: 'Bạn có thể xin lại, nhưng sẽ về cuối hàng chờ.',
    withdrawConfirmWhyApproved: 'Chủ nhà đã đồng ý. Xin lại sau có thể nhận câu trả lời hoặc giá khác.',
    withdrawKeep: 'Giữ lại',
    withdrawYes: 'Hủy',
    quoteUntil: 'Vui lòng xác nhận trước {date}.',
    requestError: 'Gửi không thành công. Vui lòng thử lại.',
    payTitleEarly: 'Nhận phòng sớm từ {time}',
    payTitleLate: 'Trả phòng muộn đến {time}',
    payCta: 'Thanh toán {price}',
    payLoading: 'Đang tải…',
    payInvalidTitle: 'Liên kết này không còn hiệu lực',
    payInvalidBody: 'Có thể nó đã được dùng, hoặc ưu đãi đã hết hạn. Vui lòng xin chủ nhà liên kết mới.',
    payDone: 'Đã thanh toán — đặt phòng của bạn đã được cập nhật.',
    payError: 'Không thể bắt đầu thanh toán. Vui lòng thử lại sau ít phút.',
    payNothingChanges: 'Chưa thanh toán thì chưa có gì thay đổi.',
    payHeaderTag: 'Yêu cầu thanh toán',
    paySecure: 'Thanh toán được xử lý an toàn qua Stripe.',
    payRowApartment: 'Căn hộ',
    payRowDates: 'Kỳ nghỉ',
    payTotal: 'Tổng',
    payDoneBody: 'Đặt phòng đã được cập nhật. Chủ nhà đã được báo.',
    payHoursInLabel: 'nhận phòng',
    payHoursOutLabel: 'trả phòng',
    introTitle: 'Bán giờ thêm hoạt động thế nào',
    introDesc: 'Khách xin đến sớm hơn hoặc đi muộn hơn ngay trong hướng dẫn nhận phòng. Bạn đặt giá, và bạn quyết định ai là người đồng ý.',
    introStep1Label: 'Khách xin',
    introStep1Desc: 'Họ chọn một giờ trong bảng giá bạn đặt bên dưới, ngay trong hướng dẫn.',
    introStep2Label: 'Có người đồng ý',
    introStep2Desc: 'Là bạn — hoặc là quy tắc, nếu bạn bật chế độ tự động cho căn hộ này.',
    introStep3Label: 'Khách thanh toán',
    introStep3Desc: 'Trên một trang riêng. Bạn cũng có thể gửi liên kết đó bằng tay.',
    introStep4Label: 'Giờ được đổi',
    introStep4Desc: 'Chỉ đến lúc này. Mã cửa được tạo lại và người dọn phòng thấy giờ mới.',
    introNote: 'Duyệt chỉ là báo giá — chưa nhận tiền thì đặt phòng chưa thay đổi gì.',
    settingsTitle: 'Nhận phòng sớm & trả phòng muộn',
    settingsIntro: 'Bán những giờ ở hai đầu kỳ nghỉ. Khách xin ngay trong hướng dẫn nhận phòng.',
    modeLabel: 'Ai duyệt',
    modeOff: 'Tắt',
    modeOffHint: 'Không hiển thị gì trong hướng dẫn.',
    modeAuto: 'Tự động',
    modeAutoHint: 'Bán ngay khi an toàn. Trường hợp khác vẫn chuyển đến bạn.',
    modeRequest: 'Theo yêu cầu',
    modeRequestHint: 'Mọi yêu cầu đều đến tay bạn.',
    cleaningLabel: 'Dọn phòng mất',
    cleaningHint: 'Được đối chiếu với từng yêu cầu. Không bao giờ ẩn giờ khỏi khách — bạn luôn thấy yêu cầu.',
    noticeLabel: 'Bán tự động dừng trước',
    noticeHint: 'ngày, để đội dọn phòng không bị bất ngờ. Yêu cầu muộn hơn sẽ chuyển đến bạn.',
    noticeZeroHint: '0 = bán đến phút chót, miễn là vẫn đủ thời gian dọn phòng.',
    earlyStairTitle: 'Giá nhận phòng sớm',
    lateStairTitle: 'Giá trả phòng muộn',
    stairHintEarly: 'Mỗi dòng áp giá cho mọi giờ từ nó đến dòng kế tiếp. Dòng sớm nhất cũng là giờ sớm nhất khách được xin.',
    stairHintLate: 'Mỗi dòng áp giá cho mọi giờ tính đến nó, ngược về dòng trước. Dòng muộn nhất cũng là giờ muộn nhất khách được xin.',
    addRow: 'Thêm giá',
    timeLabelFrom: 'Từ',
    timeLabelTo: 'Đến',
    priceLabel: 'Giá (CZK)',
    noRows: 'Chưa có giá — khách sẽ không thấy lựa chọn này.',
    remove: 'Xóa',
    saved: 'Đã lưu',
    pendingTitle: 'Yêu cầu đang chờ',
    pendingEmpty: 'Không có yêu cầu nào.',
    pendingIntro: 'Khách không biết gì cho đến khi bạn trả lời, nên nên xử lý sớm.',
    vFits: 'Vừa đủ',
    vTight: 'Sát nút',
    vWontFit: 'Không đủ',
    vConflict: 'Xung đột',
    vShortNotice: 'Báo quá gấp',
    vSpare: 'còn dư {time}',
    vShort: 'thiếu {time}',
    vNobodyArriving: 'hôm đó không có ai đến',
    vOtherGuestHolds: 'khách khác đã giữ những giờ này',
    vNoticeDaysShort: 'báo trước {days} ngày',
    legendThisStay: 'Lượt này',
    legendAsked: 'Đang xin',
    legendNeighbour: 'Khách kế / khách trước',
    legendCleaning: 'Dọn phòng',
    legendFolded: 'Trống, thu gọn',
    noPaymentAccount: 'Căn hộ này chưa nhận được thanh toán, khách sẽ không trả được.',
    noPaymentAccountFix: 'Thiết lập thanh toán',
    legendClash: 'Xung đột',
    barOut: 'trả {time}',
    barAskedAt: 'xin {time}',
    barNextIn: 'sau {time}',
    barPrevOut: 'trước {time}',
    barNormal: 'thường {time}',
    approveAnyway: 'Vẫn duyệt',
    approveCta: 'Duyệt {time}',
    counterCta: 'Đề nghị giờ khác',
    declineCta: 'Từ chối',
    counterTitle: 'Đề nghị giờ khác',
    counterAsked: 'đã xin',
    counterPriceLabel: 'Giá cho giờ này',
    counterConfirm: 'Đề nghị {time}',
    actionError: 'Không thành công. Vui lòng tải lại trang và thử lại.',

    // ── Giữ hành lý ──────────────────────────────────────────────────────
    productHours: 'Giờ thêm',
    productHoursDesc: 'Bán thêm giờ trước khi nhận phòng hoặc sau khi trả phòng. Bị giới hạn bởi thời gian dọn phòng.',
    productLuggage: 'Giữ hành lý',
    productLuggageDesc: 'Chỗ để hành lý ngoài giờ thông thường. Không tốn thời gian dọn phòng, nên bạn thường có thể đồng ý ở đây ngay cả khi phải từ chối ở trên.',
    luggageOffHint: 'Không cung cấp.',
    luggageAutoHint: 'Khách đặt giữ hành lý ngay với giá của bạn.',
    luggageRequestHint: 'Mọi yêu cầu đến bạn trước.',
    dropoffStairTitle: 'Hành lý có thể gửi từ',
    pickupStairTitle: 'Hành lý có thể để đến',
    stairHintDropoff: 'Mốc sớm nhất cũng là giới hạn — không ai xin gửi sớm hơn được.',
    stairHintPickup: 'Mốc muộn nhất cũng là giới hạn — không ai xin để lâu hơn được.',
    luggageWhereLabel: 'Để hành lý ở đâu',
    luggageWhereHint: 'Chỉ khách đã trả tiền giữ hành lý mới thấy. Ghi rõ để ở đâu và vào bằng cách nào.',
    luggageWherePlaceholder: 'ví dụ: Trong tủ bên trái lối vào, mã 1234. Vui lòng dán nhãn hành lý.',
    luggageWhereEnLabel: 'Bản tiếng Anh',
    luggageWhereMissing: 'Khách mua dịch vụ sẽ không biết để hành lý ở đâu. Hãy thêm hướng dẫn trước lần bán đầu tiên.',
    askHeaderLuggage: 'Cần chỗ để hành lý?',
    luggageDropoffTitle: 'Gửi hành lý sớm',
    luggagePickupTitle: 'Để hành lý sau khi trả phòng',
    chipLuggageDropoff: 'Gửi hành lý',
    chipLuggagePickup: 'Lấy hành lý',
    luggageWhereTitle: 'Để hành lý ở đâu',
    dNoSpace: 'Chủ nhà không còn chỗ giữ hành lý hôm đó.',
    mNoSpace: 'Không còn chỗ để hành lý',
    rdLuggageDropoff: 'Nhận hành lý',
    rdLuggagePickup: 'Lấy hành lý',
    rdClosedLuggageIn: 'Gửi hành lý sớm đã đóng, không nhận yêu cầu mới',
    rdClosedLuggageOut: 'Lấy hành lý muộn đã đóng, không nhận yêu cầu mới',
    payTitleLuggageIn: 'Giữ hành lý từ {time}',
    payTitleLuggageOut: 'Giữ hành lý đến {time}',
    payLuggageInLabel: 'Hành lý từ',
    payLuggageOutLabel: 'Hành lý đến',
    vLuggageNoClash: 'Chỉ hành lý — không ảnh hưởng lịch dọn phòng',
  },

  uk: {
    askHeaderBoth: 'Потрібне раніше заселення або пізніше виселення?',
    askHeaderEarly: 'Потрібне раніше заселення?',
    askHeaderLate: 'Потрібне пізніше виселення?',
    timesStandard: 'Стандартно',
    timesYours: 'Ваш час',
    timesIn: 'заїзд',
    timesOut: 'виїзд',
    chipCheckin: 'Заселення',
    chipCheckout: 'Виселення',
    chipFrom: 'з {time}',
    chipTill: 'до {time}',
    chipsStillAvailable: 'Ще доступно',
    chipsCanStillAsk: 'Ви ще можете попросити',
    reqYours: 'Ваш запит',
    reqApproved: 'Господар погодив',
    reqNotPossible: 'Неможливо',
    reqWaiting: 'очікує…',
    remindCta: 'Нагадати господарю',
    remindWait: 'Нагадати знову можна після {time}',
    remindSent: 'Господарю нагадали.',
    dCleaning: 'Господар не встигне підготувати квартиру до цього часу.',
    dOtherBooking: 'Того дня квартира потрібна для іншого бронювання.',
    dShortNotice: 'Це надто близько до вашого перебування, щоб устигнути це влаштувати.',
    dUnspecified: 'На жаль, цього разу це неможливо.',
    declineTitle: 'Відхилити запит',
    declineGuestSees: 'Гість побачить: «{text}»',
    declineCancel: 'Назад',
    declineConfirm: 'Відхилити',
    mCleaning: 'Не вистачить часу на прибирання',
    mOtherBooking: 'Потрібно для іншого бронювання',
    mShortNotice: 'Замало часу',
    mUnspecified: 'Без пояснення',
    scopeLabelEarly: 'Закрити й пізніші години для цього перебування',
    scopeLabelLate: 'Закрити й раніші години для цього перебування',
    scopeHintEarly: 'Гість ще може попросити пізніший приїзд, який потребує менше підготовки, ніж {time}.',
    scopeHintLate: 'Гість ще може попросити раніший від’їзд, що залишає більше часу на прибирання, ніж {time}.',
    scopeHintNotice: 'Пізній запит стосується всіх годин, не лише цієї — зніміть позначку, якщо менший запит ви б прийняли.',
    scopeAuto: 'авто',
    rdTitle: 'Додаткові години',
    rdCheckin: 'Заселення',
    rdCheckout: 'Виселення',
    rdStandard: 'стандартно',
    rdPaid: 'оплачено · {price} · {date}',
    rdClosedEarly: 'Раннє заселення закрите для нових запитів',
    rdClosedLate: 'Пізнє виселення закрите для нових запитів',
    rdClosedDetail: 'Відхилено {date} — {reason} Гість не зможе попросити знову, доки ви не відкриєте.',
    rdReopen: 'Знову дозволити запити',
    rdWaitingManager: 'просить {time} · очікує на вашу відповідь з {since}',
    rdWaitingPayment: '{time} схвалено · {price} · очікує оплати до {until}',
    earlyTitle: 'Приїхати раніше',
    lateTitle: 'Лишитися довше',
    instantBadge: 'забронювати',
    askBadge: 'запитати',
    free: 'безкоштовно',
    noteLabel: 'Хочете щось передати господарю?',
    notePlaceholder: 'напр. літак сідає о 09:40',
    submitting: 'Надсилаємо…',
    statusPaid: 'Підтверджено.',
    statusExpired: 'Термін пропозиції минув.',
    payNow: 'Підтвердити та оплатити',
    withdraw: 'Скасувати запит',
    withdrawConfirmQ: 'Скасувати запит на {time}?',
    withdrawConfirmWhy: 'Можна попросити знову, але запит піде в кінець черги.',
    withdrawConfirmWhyApproved: 'Господар вже погодився. Пізніше відповідь або ціна можуть бути іншими.',
    withdrawKeep: 'Залишити',
    withdrawYes: 'Скасувати',
    quoteUntil: 'Підтвердьте, будь ласка, до {date}.',
    requestError: 'Не вдалося надіслати. Спробуйте ще раз.',
    payTitleEarly: 'Раннє заселення з {time}',
    payTitleLate: 'Пізнє виселення до {time}',
    payCta: 'Оплатити {price}',
    payLoading: 'Завантаження…',
    payInvalidTitle: 'Це посилання вже недійсне',
    payInvalidBody: 'Можливо, ним уже скористалися, або пропозиція минула. Попросіть у господаря нове.',
    payDone: 'Оплачено — бронювання оновлено.',
    payError: 'Не вдалося розпочати оплату. Спробуйте, будь ласка, за кілька хвилин.',
    payNothingChanges: 'Доки платіж не пройде, нічого не змінюється.',
    payHeaderTag: 'Запит на оплату',
    paySecure: 'Оплату безпечно обробляє Stripe.',
    payRowApartment: 'Апартаменти',
    payRowDates: 'Перебування',
    payTotal: 'Разом',
    payDoneBody: 'Бронювання оновлено. Господаря повідомлено.',
    payHoursInLabel: 'заїзд',
    payHoursOutLabel: 'виїзд',
    introTitle: 'Як працює продаж додаткових годин',
    introDesc: 'Гості просять про раніший приїзд або пізніший від’їзд просто в путівнику. Ціни встановлюєте ви — і ви ж вирішуєте, хто погоджує.',
    introStep1Label: 'Гість просить',
    introStep1Desc: 'Обирає годину з цін, які ви задасте нижче, просто в путівнику.',
    introStep2Label: 'Хтось погоджує',
    introStep2Desc: 'Ви — або правило, якщо ввімкнути для цієї квартири автоматичний режим.',
    introStep3Label: 'Гість платить',
    introStep3Desc: 'На окремій сторінці. Це посилання можна надіслати й вручну.',
    introStep4Label: 'Час змінюється',
    introStep4Desc: 'Аж тепер. Код від дверей перевидається, а прибиральник бачить новий час.',
    introNote: 'Погодження — це лише пропозиція: доки гроші не надійдуть, у бронюванні нічого не змінюється.',
    settingsTitle: 'Раннє заселення та пізнє виселення',
    settingsIntro: 'Продавайте години на обох кінцях перебування. Гості просять просто з путівника.',
    modeLabel: 'Хто погоджує',
    modeOff: 'Вимкнено',
    modeOffHint: 'У путівнику нічого не з’явиться.',
    modeAuto: 'Автоматично',
    modeAutoHint: 'Продавати одразу, коли це безпечно. Решта все одно приходить вам.',
    modeRequest: 'За запитом',
    modeRequestHint: 'Кожен запит приходить вам.',
    cleaningLabel: 'Прибирання триває',
    cleaningHint: 'Звіряється з кожним запитом. Ніколи не ховає годину від гостя — запит ви побачите завжди.',
    noticeLabel: 'Автоматичний продаж припиняється за',
    noticeHint: 'днів до перебування, щоб прибиральники не мали несподіванок. Пізніші запити приходять вам.',
    noticeZeroHint: '0 = продавати до останньої хвилини, доки вистачає часу на прибирання.',
    earlyStairTitle: 'Ціни за раннє заселення',
    lateStairTitle: 'Ціни за пізнє виселення',
    stairHintEarly: 'Кожен рядок задає ціну для всіх годин від себе до наступного рядка. Найраніший рядок — це й найраніший час, який можна попросити.',
    stairHintLate: 'Кожен рядок задає ціну для всіх годин до себе, назад до попереднього рядка. Найпізніший рядок — це й найпізніший час, який можна попросити.',
    addRow: 'Додати ціну',
    timeLabelFrom: 'Від',
    timeLabelTo: 'До',
    priceLabel: 'Ціна (CZK)',
    noRows: 'Цін ще немає — гості не побачать цієї можливості.',
    remove: 'Видалити',
    saved: 'Збережено',
    pendingTitle: 'Запити в очікуванні',
    pendingEmpty: 'Нічого не очікує.',
    pendingIntro: 'Доки ви не відповісте, гість не знає нічого — тож швидка відповідь окупається.',
    vFits: 'Вистачає',
    vTight: 'Впритул',
    vWontFit: 'Не вистачає',
    vConflict: 'Конфлікт',
    vShortNotice: 'Замало часу',
    vSpare: 'лишається {time}',
    vShort: 'бракує {time}',
    vNobodyArriving: 'того дня ніхто не приїздить',
    vOtherGuestHolds: 'ці години вже має інший гість',
    vNoticeDaysShort: 'попереджено за {days} дн.',
    legendThisStay: 'Це перебування',
    legendAsked: 'Запит',
    legendNeighbour: 'Наступний / попередній гість',
    legendCleaning: 'Прибирання',
    legendFolded: 'Порожньо, скорочено',
    noPaymentAccount: 'Ці апартаменти ще не приймають платежі, гість не зможе оплатити.',
    noPaymentAccountFix: 'Налаштувати платежі',
    legendClash: 'Конфлікт',
    barOut: 'виїзд {time}',
    barAskedAt: 'просить {time}',
    barNextIn: 'наст. {time}',
    barPrevOut: 'попер. {time}',
    barNormal: 'звично {time}',
    approveAnyway: 'Все одно погодити',
    approveCta: 'Погодити {time}',
    counterCta: 'Запропонувати інший час',
    declineCta: 'Відхилити',
    counterTitle: 'Запропонувати інший час',
    counterAsked: 'просив',
    counterPriceLabel: 'Ціна за цей час',
    counterConfirm: 'Запропонувати {time}',
    actionError: 'Не вдалося. Перезавантажте сторінку і спробуйте ще раз.',

    // ── Зберігання багажу ────────────────────────────────────────────────
    productHours: 'Додаткові години',
    productHoursDesc: 'Продавайте години до заселення та після виселення. Обмежено тим, скільки триває прибирання.',
    productLuggage: 'Зберігання багажу',
    productLuggageDesc: 'Місце для валіз поза звичайними годинами. Це не забирає час на прибирання, тож тут ви часто можете погодитися навіть тоді, коли вище доводиться відмовити.',
    luggageOffHint: 'Не пропонується.',
    luggageAutoHint: 'Гості одразу купують зберігання за вашою ціною.',
    luggageRequestHint: 'Кожен запит спершу надходить вам.',
    dropoffStairTitle: 'Багаж можна залишити з',
    pickupStairTitle: 'Багаж може лишатися до',
    stairHintDropoff: 'Найраніший рядок є й межею — раніше попросити не можна.',
    stairHintPickup: 'Найпізніший рядок є й межею — пізніше попросити не можна.',
    luggageWhereLabel: 'Куди класти багаж',
    luggageWhereHint: 'Бачать лише гості, які оплатили зберігання. Напишіть точно, куди класти й як потрапити всередину.',
    luggageWherePlaceholder: 'напр. У шафі ліворуч біля входу, код 1234. Підпишіть, будь ласка, свої валізи.',
    luggageWhereEnLabel: 'Англійська версія',
    luggageWhereMissing: 'Гості, які куплять зберігання, не дізнаються, куди класти багаж. Додайте інструкції до першого продажу.',
    askHeaderLuggage: 'Потрібне місце для багажу?',
    luggageDropoffTitle: 'Залишити багаж раніше',
    luggagePickupTitle: 'Лишити багаж після виселення',
    chipLuggageDropoff: 'Залишити',
    chipLuggagePickup: 'Забрати',
    luggageWhereTitle: 'Куди класти багаж',
    dNoSpace: 'Того дня у господаря немає місця для багажу.',
    mNoSpace: 'Немає місця для багажу',
    rdLuggageDropoff: 'Прийом багажу',
    rdLuggagePickup: 'Видача багажу',
    rdClosedLuggageIn: 'Ранній прийом багажу закритий для нових запитів',
    rdClosedLuggageOut: 'Пізня видача багажу закрита для нових запитів',
    payTitleLuggageIn: 'Зберігання багажу з {time}',
    payTitleLuggageOut: 'Зберігання багажу до {time}',
    payLuggageInLabel: 'Багаж з',
    payLuggageOutLabel: 'Багаж до',
    vLuggageNoClash: 'Лише багаж — на зміну гостей не впливає',
  },
};
